import _, {memoize} from 'lodash';
import {connect} from 'react-redux';

import {
  getTimeFormat,
  getUserTimeZone,
} from '../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CalendarsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {withUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {ThemeContextType} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {StartEndData} from '../../../../srcCommon/helpers/calendarHelpers';
import {CombinedCalendarEventParameters} from '../../../../srcCommon/helpers/models/calendarEventVersionHelpers';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {AppState} from '../../../store/appState';
import {makeGetSelectedSideFrameId} from '../../../store/data/applications/selectedSideFrameIdSelectors';
import {rescheduleCalendarEventVersionAction} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {makeFindGetCalendarEventParametersList} from '../../../store/data/calendarEventVersions/calendarEventVersionsSelectors';
import {
  getArbitraryCalendarEvents,
  getCalendarEvents,
  getCalendarEventVersions,
  getCalendars,
  getCalendarVersions,
} from '../../../store/data/dataSelectors';
import {
  createOrUpdateCalendarEventDraftFromSlotAction,
  highlightCalendarEventAction,
  popCalendarEventAction,
} from '../../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarEventData,
  makeGetCalendarsEventVersionUids,
  makeGetCalendarsPanel,
  makeGetCalendarsParameters,
  makeGetFocusedCalendarEventParameters,
  makeGetHighlightedCalendarEventParameters,
  makeGetPopoverCalendarEventParameters,
  makeGetViewingNowCalendars,
} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {makeGetAppPanelIdFromPanel} from '../../../store/panels/panelsSelectors';
import {CalendarsGrid, SchedulingSlotDefinition} from './calendarsGrid';
import {
  buildFullCalendarArbitraryEvent,
  buildFullCalendarEvent,
  buildFullCalendarSlot,
} from './fullCalendarHelpers';

/*
 * Props.
 */

interface CalendarsGridContainerProps extends ThemeContextType {
  panelId: CalendarsPanelId;
  onCalendarSlotSelect?: (data: StartEndData) => void;
  selectedSlots?: ReadonlyArray<SchedulingSlotDefinition>;
}

/*
 * Selectors.
 */

function getTheme(_state: AppState, props: CalendarsGridContainerProps) {
  return props.theme;
}

function getSelectedSlots(_state: AppState, props: CalendarsGridContainerProps) {
  return props.selectedSlots;
}

type MakeGetCombinedCalendarEventParameters = AppParametricSelector<any, CombinedCalendarEventParameters>;
function makeGetCombinedEventParameters(
  getHighlightedCalendarEventParameters: ReturnType<typeof makeGetPopoverCalendarEventParameters>,
  getFocusedCalendarEventParameters: ReturnType<typeof makeGetFocusedCalendarEventParameters>,
  getPopoverCalendarEventParameters: ReturnType<typeof makeGetPopoverCalendarEventParameters>,
) {
  return createSelector(
    [
      getHighlightedCalendarEventParameters,
      getFocusedCalendarEventParameters,
      getPopoverCalendarEventParameters,
    ],
    (highlightedCalendarEventParameters, focusedCalendarEventParameters, popoverCalendarEventParameters) => ({
      highlightedEventParameters: highlightedCalendarEventParameters,
      focusedCalendarEventParameters,
      popoverCalendarEventParameters,
    }),
  );
}

function makeGetFindFullCalendarEvents(
  getCalendarsParameters: ReturnType<typeof makeGetCalendarsParameters>,
  getFindCalendarEventParametersList: ReturnType<typeof makeFindGetCalendarEventParametersList>,
  getCombinedEventParameters: MakeGetCombinedCalendarEventParameters,
  getFocusedCalendarEventData: ReturnType<typeof makeGetCalendarEventData>,
  getViewingNowCalendars: ReturnType<typeof makeGetViewingNowCalendars>,
) {
  return createSelector(
    [
      getCalendarEventVersions,
      getArbitraryCalendarEvents,
      getCalendarEvents,
      getCalendarVersions,
      getCalendars,
      getCalendarsParameters,
      getFindCalendarEventParametersList,
      getCombinedEventParameters,
      getFocusedCalendarEventData,
      getViewingNowCalendars,
      getSelectedSlots,
      getTheme,
    ],
    (
      calendarEventVersions,
      arbitraryCalendarEvents,
      calendarEvents,
      calendarVersions,
      calendars,
      parameters,
      findCalendarEventParametersList,
      combinedEventParameters,
      focusedCalendarEventData,
      viewingNowCalendars,
      selectedSlots,
      theme,
    ) =>
      memoize(() => {
        // If a calendarVersion is displayed in the viewing now section, ensure that we display events using that color.
        const viewingNowCalendarVersionToColor = _(viewingNowCalendars)
          .filter((v) => Boolean(v.calendarVersionId))
          .keyBy((v) => v.calendarVersionId || 0)
          .mapValues((v) => v.color)
          .value();

        const viewingNowAddressToColor = _(viewingNowCalendars)
          .keyBy((v) => v.address)
          .mapValues((v) => v.color)
          .value();

        const fullCalendarEvents = _(findCalendarEventParametersList())
          .map((p) => {
            const calendarEventVersion = calendarEventVersions[p.calendarEventVersionUid];
            const calendarVersion = calendarVersions[p.calendarVersionId];
            if (!calendarEventVersion || !calendarVersion) {
              return null;
            }

            const calendarEvent = calendarEvents[calendarEventVersion.calendarEventUid];
            const calendar = calendars[calendarVersion.calendarId];
            if (!calendarEvent || !calendar) {
              return null;
            }

            return buildFullCalendarEvent(
              parameters.viewType,
              calendarEventVersion,
              calendarEvent,
              calendarVersion,
              calendar,
              p.instanceStartDate,
              combinedEventParameters,
              theme,
              viewingNowCalendarVersionToColor[calendarVersion.id],
            );
          })
          .compact()
          .value();

        const fullCalendarArbitraryEvents = _(arbitraryCalendarEvents)
          .values()
          .flatten()
          .compact()
          .value()
          .filter((event) => !event.removed)
          .filter((event) => parameters.arbitraryCalendarAddresses.includes(event.address))
          .map((event) => {
            const maybeCalendarVersion = event.calendarVersionId
              ? calendarVersions[event.calendarVersionId]
              : undefined;

            return buildFullCalendarArbitraryEvent(
              event,
              theme,
              parameters.viewType,
              maybeCalendarVersion?.color,
              viewingNowAddressToColor,
            );
          });

        const extendedFullCalendarEvents = [...fullCalendarEvents, ...fullCalendarArbitraryEvents];

        // Also render focused events being edited that are not already in the event list
        if (
          focusedCalendarEventData &&
          !extendedFullCalendarEvents.some(
            (event) => event.calendarEventVersionUid === focusedCalendarEventData.calendarEventVersion.uid,
          )
        ) {
          const {calendarEventVersion, calendarVersion, instanceStartDate} = focusedCalendarEventData;
          const focusedFullCalendarEvent = buildFullCalendarEvent(
            parameters.viewType,
            calendarEventVersion,
            focusedCalendarEventData.calendarEvent,
            calendarVersion,
            focusedCalendarEventData.calendar,
            instanceStartDate,
            combinedEventParameters,
            theme,
          );

          return [...extendedFullCalendarEvents, focusedFullCalendarEvent];
        }

        // If we scheduling slots, also render them in the calendar grid.
        if (selectedSlots) {
          const selectedSlotEvents = selectedSlots.map((slot) =>
            buildFullCalendarSlot(slot.id, slot.start, slot.end, theme),
          );

          return [...extendedFullCalendarEvents, ...selectedSlotEvents];
        }

        return extendedFullCalendarEvents;
      }),
  );
}

const getFindCalendarEventForCalendarEventVersion = createSelector(
  [getCalendarEvents, getCalendarEventVersions],
  (calendarEvents, calendarEventVersions) =>
    memoize((calendarEventVersionUid: string) => {
      const calendarEventVersion = calendarEventVersions[calendarEventVersionUid];
      if (!calendarEventVersion) {
        return undefined;
      }

      return calendarEvents[calendarEventVersion.calendarEventUid];
    }),
);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CalendarsGridContainerProps>()(() => {
  const getCalendarsPanel = makeGetCalendarsPanel();
  const getCalendarsParameters = makeGetCalendarsParameters(getCalendarsPanel);
  const getCalendarsEventVersionUids = makeGetCalendarsEventVersionUids(getCalendarsPanel);

  const calendarEventParametersList = makeFindGetCalendarEventParametersList(
    getCalendarsParameters,
    getCalendarsEventVersionUids,
  );
  const getHighlightedCalendarEventParameters = makeGetHighlightedCalendarEventParameters(getCalendarsPanel);
  const getFocusedCalendarEventParameters = makeGetFocusedCalendarEventParameters(getCalendarsPanel);
  const getPopoverCalendarEventParameters = makeGetPopoverCalendarEventParameters(getCalendarsPanel);

  const getCombinedEventParameters = makeGetCombinedEventParameters(
    getHighlightedCalendarEventParameters,
    getFocusedCalendarEventParameters,
    getPopoverCalendarEventParameters,
  );

  const getViewingNowCalendars = makeGetViewingNowCalendars(getCalendarsPanel);

  const getHighlightedCalendarEventData = makeGetCalendarEventData(getHighlightedCalendarEventParameters);
  const getFocusedCalendarEventData = makeGetCalendarEventData(getFocusedCalendarEventParameters);

  const getFindFullCalendarEvents = makeGetFindFullCalendarEvents(
    getCalendarsParameters,
    calendarEventParametersList,
    getCombinedEventParameters,
    getFocusedCalendarEventData,
    getViewingNowCalendars,
  );

  return {
    parameters: getCalendarsParameters,
    highlightedCalendarEventData: getHighlightedCalendarEventData,
    focusedCalendarEventData: getFocusedCalendarEventData,
    findFullCalendarEvents: getFindFullCalendarEvents,
    selectedFrameId: makeGetSelectedSideFrameId(makeGetAppPanelIdFromPanel()),
    timeFormat: getTimeFormat,
    timeZone: getUserTimeZone,
    findCalendarEventForCalendarEventVersion: getFindCalendarEventForCalendarEventVersion,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<CalendarsGridContainerProps>()(({panelId}) => ({
  onHighlightedEventClear: () => highlightCalendarEventAction(panelId, undefined),
  onPopoverEventClear: () => popCalendarEventAction(panelId, undefined),
  onCalendarSlotSelect: (slot: StartEndData) => createOrUpdateCalendarEventDraftFromSlotAction(panelId, slot),
  onEventReschedule: rescheduleCalendarEventVersionAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: CalendarsGridContainerProps,
) {
  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    // Fallback to the dispatch method if not provided.
    onCalendarSlotSelect: ownProps.onCalendarSlotSelect || dispatchProps.onCalendarSlotSelect,
  };
}

export const CalendarsGridContainer = withUserTheme(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(CalendarsGrid),
);
